import { isDigits } from '../../helpers/string';

type screenType = 'pt-dashboard' | 'wait-screen' | 'unassigned' | 'leaderboard' | 'lights' | 'composite-zone-screen';
type zoneNumber = number;

export interface IScreenData {
  height: string | 'auto';
  width: string | 'auto';
  zone: zoneNumber | screenType;
  innerzones?: zoneNumber[]; // only used if zone is composite-zone-screen
  url: string;
}

export interface ICompositeScreenData extends IScreenData {
  rowNumber: number;
  startsInCellNumber: number;
  endsInCellNumber: number;
  /**
   * added for easier ui access, while `endsInCellNumber` makes test a bit easier
   * `grid-column-end` wants the number (index+1) of the cell *after* this cell: a cell
   * that starts at 1 and ends at 3 is 2 cells wide
   * `span <cellSpan>` is used the ui
   */
  cellSpan: number;
}

export interface ICompositeScreen {
  screens: ICompositeScreenData[];
  gridColumns: number;
  rowCount: number;
  rowsToDisplay: number;
}

export const isNotSingleZoneScreenType = (str: string): str is screenType => {
  const supportedTypes: screenType[] = [
    'pt-dashboard', 'wait-screen', 'unassigned', 'leaderboard', 'lights', 'composite-zone-screen',
  ];
  return supportedTypes.indexOf(str as screenType) > -1;
};

interface ICompositeScreenParams {
  cacheBustId: string;
  innerzonesParam: string;
  columns: number;
  origin: string;
  gymName: string;
  deviceId: string;
}

export const parseInnerZonesParamForCompositeScreenData = ({
  cacheBustId,
  innerzonesParam,
  columns,
  origin,
  gymName,
  deviceId,
}: ICompositeScreenParams): ICompositeScreen => {
  const iframeCellSpan = 2;

  const innerzones = innerzonesParam.split('-').map(Number);
  const parsedScreens: ICompositeScreenData[] = [];
  const showOnlySummary = true;

  function calculateEmptyCellsToAddAsSpacingBeforeFirstColumnInLastRow() {
    const columnsInLastRow = innerzones.length % columns;
    const emptyColumnsInLastRow = columns - columnsInLastRow;
    const emptyCellsInLastRow = emptyColumnsInLastRow * iframeCellSpan;
    return emptyCellsInLastRow / 2;
  }

  const lastRowStartIndex = Math.floor(innerzones.length / columns) * columns;
  const emptyCellsToAddBeforeFirstScreenInLastRow = calculateEmptyCellsToAddAsSpacingBeforeFirstColumnInLastRow();

  for (let index = 0; index < innerzones.length; index++) {
    const zonenumber = innerzones[index];

    const row = Math.floor(index / columns);
    const column = index % columns;
    let startCellIndex = column * iframeCellSpan;
    if (index >= lastRowStartIndex) {
      startCellIndex += emptyCellsToAddBeforeFirstScreenInLastRow;
    }

    parsedScreens.push({
      height: '100%',
      width: '100%',
      zone: zonenumber,
      url: getURLForScreen(
        cacheBustId,
        zonenumber,
        origin,
        gymName,
        deviceId,
        /* inner zones */ undefined,
        showOnlySummary,
        columns,
      ),
      rowNumber: row + 1,
      startsInCellNumber: startCellIndex + 1,
      endsInCellNumber: startCellIndex + iframeCellSpan,
      cellSpan: iframeCellSpan,
    });
  }

  const rowCount = parsedScreens[parsedScreens.length - 1].rowNumber;
  function calculateRowsToDisplay() {
    if (columns > 2 && rowCount === 1) {
      // shrink the available space in order for 3x1 and 4x1 grids to display the cells better
      return 2;
    }
    return rowCount;
  }

  return {
    screens: parsedScreens,
    gridColumns: columns * iframeCellSpan,
    rowCount,
    rowsToDisplay: calculateRowsToDisplay(),
  };
};

export const parseScreensParamForScreenData = (
  cacheBustId: string,
  screensParam: string,
  origin: string,
  gymName: string,
  deviceId: string,
): IScreenData[] => {
  try {
    const screens = screensParam.split(',');
    const parsedScreens: IScreenData[] = [];
    for (const screen of screens) {
      const [zoneScreenSpec, screenSize, columnCountParam] = screen.split('--');
      const zoneScreenParts = zoneScreenSpec.split('_');
      const zoneType = zoneScreenParts.shift();
      if (!zoneType) {
        throw new Error('Expected zone type or number in screens');
      }
      const innerzones = zoneScreenParts;
      const [width, height] = screenSize.split('x');

      if (!width || !height) {
        throw new Error('Invalid height or width');
      }

      let columnCount: number | undefined;
      if (columnCountParam && !columnCountParam.startsWith('cols')) {
        throw new Error(
          'Invalid column count specified: the 3rd split in screen should be prefixed with "cols"'
          + ', ex "...--...--cols3"',
        );
      } else if (columnCountParam) {
        columnCount = parseInt(columnCountParam.substring(4), 10);
      }

      let parsedZone: zoneNumber | screenType;
      let parsedInnerzones: zoneNumber[] | undefined;
      if (isNotSingleZoneScreenType(zoneType)) {
        parsedZone = zoneType;
        if (zoneType === 'composite-zone-screen') {
          if (!innerzones || innerzones.length === 0) {
            throw new Error(`Expected innerzones in ${zoneType}. eg add _1_2_3 to the end of the zone spec`);
          }
          if (!columnCount) {
            columnCount = 3;
          }
          if (columnCount < 2 || columnCount > 4) {
            throw new Error(`Invalid column count specified for ${zoneType}`);
          }
          parsedInnerzones = innerzones.map((zone) => parseInt(zone, 10));
        }
      } else if (isDigits(zoneType)) {
        parsedZone = parseInt(zoneType, 10);
      } else {
        throw new Error('Invalid zone type');
      }

      const showOnlySummary = undefined;
      parsedScreens.push({
        height: height === 'auto' ? '100%' : height,
        width: width === 'auto' ? calculateAutoScreenPercent(screens.length) : width,
        zone: parsedZone,
        innerzones: parsedInnerzones,
        url: getURLForScreen(
          cacheBustId,
          parsedZone,
          origin,
          gymName,
          deviceId,
          parsedInnerzones,
          showOnlySummary,
          columnCount,
        ),
      });
    }
    guardMixedUseOfAutoAndFixedWidth(parsedScreens);
    return parsedScreens;
  } catch (e) {
    throw new Error(`Invalid value for screens param: ${e.message}. `
      + 'Expected format for each screen: '
      + '[zoneNumber|pt-dashboard|wait-screen|unassigned|leaderboard|lights]--[width|auto]x[height|auto]. '
      + 'Multiple screens can be seperated by a comma. Eg of correctly formatted screens param: '
      + 'screens=2--1920x1080,3--1280x720,leaderboard--1920x1080 '
      + 'for a three screen setup that includes a zone2, zone3 and leaderboard screens. '
      + 'if all the displays are consistent you can use "auto" for the width and height '
      + 'eg screens=2--autoxauto,3--autoxauto. But you cannot mix "auto" width and a fixed width');
  }
};

const guardMixedUseOfAutoAndFixedWidth = (screens: IScreenData[]) => {
  if (screenDataContainsAutoWidth(screens)) {
    const screenWithFixedWidth = screens.find((screen) => isDigits(screen.width));
    if (screenWithFixedWidth) {
      throw new Error(`Mixed screen width "auto" and ${screenWithFixedWidth.width} not allowed`);
    }
  }
};

const calculateAutoScreenPercent = (numberOfScreensOnAxis: number): string => `${100 / numberOfScreensOnAxis}%`;

const getURLForScreen = (
  cacheBustId: string,
  zone: screenType | zoneNumber,
  origin: string,
  gymName: string,
  deviceId: string,
  innerzones?: zoneNumber[],
  showOnlySummary?: boolean,
  columnCount?: number | undefined,
): string => {
  const cleanedCacheBustId = cacheBustId.replace(new RegExp('[-:.]', 'g'), '');
  const staticRoot = `/static/${cleanedCacheBustId}/`;
  let path = staticRoot;

  const params = new URLSearchParams();
  params.append('gymName', gymName);
  params.append('deviceId', deviceId);
  if (typeof zone === 'number') {
    params.append('zoneNumber', zone.toString(10));
    params.append('htmlVideo', 'true');

    if (columnCount) {
      params.append('columns', columnCount.toString());
    }

    if (showOnlySummary) {
      path = `${staticRoot}machine-zone.html`;
    }
  } else {
    path = `${staticRoot}${zone}.html`;

    if (zone === 'composite-zone-screen' && innerzones) {
      params.append('innerzones', innerzones.join('-'));
      if (columnCount) {
        params.append('columns', columnCount.toString());
      }
    }
  }
  return `${origin + path}?${params.toString()}`;
};

const screenDataContainsAutoWidth = (screenData: IScreenData[]): boolean => !!(screenData
  .find((screen) => screen.width.includes('%'))
);

export const getScreensForWindowWidth = (
  requestedScreens: IScreenData[],
  windowWidth: number,
): IScreenData[] => {
  if (screenDataContainsAutoWidth(requestedScreens)) {
    return requestedScreens;
  }

  const allocatedScreens = [...requestedScreens];
  let allocatedScreenWidth = requestedScreens.reduce((acc, { width }) => acc + parseInt(width, 10), 0);

  while (allocatedScreenWidth > windowWidth && allocatedScreens.length > 0) {
    const removedScreen = allocatedScreens.pop();
    allocatedScreenWidth -= parseInt(removedScreen!.width, 10);
  }

  if (allocatedScreens.length === 0) {
    throw new Error('Not enough window width for requested screens, check screen resolution and screens url param');
  }

  return allocatedScreens;
};
